import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { MainLayout } from "@/components/MainLayout";
import { usePortfolio } from "@/context/PortfolioContext";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { PageLoader } from "@/components/MoneyMachineLoader";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import {
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  PiggyBank,
  AlertTriangle,
  Info,
  CreditCard,
  RefreshCw,
  Lock,
  LineChart,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Income categories
const INCOME_CATEGORIES = [
  { value: "salary", label: "Salary", icon: Wallet },
  { value: "yield", label: "Investment Yield", icon: TrendingUp },
  { value: "rental", label: "Rental Income", icon: ArrowDownCircle },
  { value: "dividend", label: "Dividends", icon: PiggyBank },
  { value: "appreciation", label: "Capital Appreciation", icon: TrendingUp },
  { value: "other", label: "Other", icon: Wallet },
];

// Passive income categories (for the passive income view)
const PASSIVE_INCOME_CATEGORIES = ["yield", "rental", "dividend", "appreciation"];

// Chart colors
const CHART_COLORS = {
  income: "#22c55e",
  expenses: "#ef4444",
  net: "#3b82f6",
  passive: "#8b5cf6",
};

const PIE_COLORS = ["#22c55e", "#3b82f6", "#8b5cf6", "#f59e0b", "#ec4899", "#14b8a6"];

// Expense categories
const EXPENSE_CATEGORIES = [
  { value: "housing", label: "Housing" },
  { value: "utilities", label: "Utilities" },
  { value: "subscription", label: "Subscriptions" },
  { value: "insurance", label: "Insurance" },
  { value: "tax", label: "Taxes" },
  { value: "transport", label: "Transport" },
  { value: "food", label: "Food & Dining" },
  { value: "entertainment", label: "Entertainment" },
  { value: "healthcare", label: "Healthcare" },
  { value: "other", label: "Other" },
];

const FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
  { value: "one_time", label: "One-time" },
];

const BudgetPage = () => {
  const { formatCurrency } = usePortfolio();
  const { t } = useI18n();
  const { isDemo } = useAuth();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [incomeSources, setIncomeSources] = useState([]);
  const [expenses, setExpenses] = useState([]);

  // Dialog states
  const [isIncomeOpen, setIsIncomeOpen] = useState(false);
  const [isExpenseOpen, setIsExpenseOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // "income" or "expense"
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [incomeForm, setIncomeForm] = useState({
    name: "",
    category: "salary",
    amount: "",
    frequency: "monthly",
    currency: "USD",
    start_date: "",
    is_active: true,
    is_passive: false,
    notes: "",
  });

  // Predictions chart state
  const [projectionMode, setProjectionMode] = useState("average"); // "average" or "recurring"

  const [expenseForm, setExpenseForm] = useState({
    name: "",
    category: "other",
    amount: "",
    frequency: "monthly",
    currency: "USD",
    start_date: "",
    is_active: true,
    notes: "",
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, incomeRes, expenseRes] = await Promise.all([
        axios.get(`${API_URL}/api/budget/budget/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/budget/income`, { withCredentials: true }),
        axios.get(`${API_URL}/api/budget/expenses`, { withCredentials: true }),
      ]);
      setSummary(summaryRes.data);
      setIncomeSources(incomeRes.data);
      setExpenses(expenseRes.data);
    } catch (error) {
      console.error("Failed to fetch budget data:", error);
      toast.error(t("budget.fetchError") || "Failed to load budget data");
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset forms
  const resetIncomeForm = () => {
    setIncomeForm({
      name: "",
      category: "salary",
      amount: "",
      frequency: "monthly",
      currency: "USD",
      start_date: "",
      is_active: true,
      is_passive: false,
      notes: "",
    });
    setEditingItem(null);
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      name: "",
      category: "other",
      amount: "",
      frequency: "monthly",
      currency: "USD",
      start_date: "",
      is_active: true,
      notes: "",
    });
    setEditingItem(null);
  };

  // Income CRUD
  const handleSaveIncome = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/api/budget/income/${editingItem.income_id}`, incomeForm, { withCredentials: true });
        toast.success(t("budget.incomeUpdated") || "Income updated");
      } else {
        await axios.post(`${API_URL}/api/budget/income`, incomeForm, { withCredentials: true });
        toast.success(t("budget.incomeAdded") || "Income added");
      }
      setIsIncomeOpen(false);
      resetIncomeForm();
      fetchData();
    } catch (error) {
      toast.error(t("budget.saveFailed") || "Failed to save");
    }
  };

  const openEditIncome = (item) => {
    setEditingItem(item);
    setIncomeForm({
      name: item.name || "",
      category: item.category || "salary",
      amount: item.amount?.toString() || "",
      frequency: item.frequency || "monthly",
      currency: item.currency || "USD",
      start_date: item.start_date || "",
      is_active: item.is_active ?? true,
      is_passive: item.is_passive ?? false,
      notes: item.notes || "",
    });
    setIsIncomeOpen(true);
  };

  // Expense CRUD
  const handleSaveExpense = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/api/budget/expenses/${editingItem.expense_id}`, expenseForm, { withCredentials: true });
        toast.success(t("budget.expenseUpdated") || "Expense updated");
      } else {
        await axios.post(`${API_URL}/api/budget/expenses`, expenseForm, { withCredentials: true });
        toast.success(t("budget.expenseAdded") || "Expense added");
      }
      setIsExpenseOpen(false);
      resetExpenseForm();
      fetchData();
    } catch (error) {
      toast.error(t("budget.saveFailed") || "Failed to save");
    }
  };

  const openEditExpense = (item) => {
    setEditingItem(item);
    setExpenseForm({
      name: item.name || "",
      category: item.category || "other",
      amount: item.amount?.toString() || "",
      frequency: item.frequency || "monthly",
      currency: item.currency || "USD",
      start_date: item.start_date || "",
      is_active: item.is_active ?? true,
      notes: item.notes || "",
    });
    setIsExpenseOpen(true);
  };

  // Delete handler
  const confirmDelete = (item, type) => {
    setDeleteTarget(item);
    setDeleteType(type);
    setIsDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget || !deleteType) return;
    try {
      if (deleteType === "income") {
        await axios.delete(`${API_URL}/api/budget/income/${deleteTarget.income_id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API_URL}/api/budget/expenses/${deleteTarget.expense_id}`, { withCredentials: true });
      }
      toast.success(t("budget.deleted") || "Deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(t("budget.deleteFailed") || "Failed to delete");
    } finally {
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      setDeleteType(null);
    }
  };

  // Health score color
  const getHealthColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getHealthBg = (score) => {
    if (score >= 80) return "bg-green-500/10 border-green-500/30";
    if (score >= 60) return "bg-yellow-500/10 border-yellow-500/30";
    if (score >= 40) return "bg-orange-500/10 border-orange-500/30";
    return "bg-red-500/10 border-red-500/30";
  };

  // Convert to monthly for display
  const toMonthly = (amount, frequency) => {
    const freqMap = { monthly: 1, biweekly: 26 / 12, quarterly: 1 / 3, yearly: 1 / 12, one_time: 0 };
    return amount * (freqMap[frequency] || 1);
  };

  // Generate predictions chart data (12 months projection)
  const projectionData = useMemo(() => {
    if (!summary || (incomeSources.length === 0 && expenses.length === 0)) return [];
    
    const months = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const futureDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthLabel = futureDate.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      
      let income = 0;
      let expense = 0;
      
      if (projectionMode === "average") {
        // Mode 1: Use current monthly averages
        income = summary.monthly_income || 0;
        expense = summary.total_monthly_outflow || 0;
      } else {
        // Mode 2: Calculate based on recurring schedules
        incomeSources.forEach((src) => {
          if (!src.is_active) return;
          const freq = src.frequency;
          const amount = src.amount || 0;
          const monthIndex = i;
          
          if (freq === "monthly") {
            income += amount;
          } else if (freq === "biweekly") {
            income += amount * (26 / 12);
          } else if (freq === "quarterly" && monthIndex % 3 === 0) {
            income += amount;
          } else if (freq === "yearly" && monthIndex === 0) {
            income += amount;
          }
        });
        
        expenses.forEach((exp) => {
          if (!exp.is_active) return;
          const freq = exp.frequency;
          const amount = exp.amount || 0;
          const monthIndex = i;
          
          if (freq === "monthly") {
            expense += amount;
          } else if (freq === "biweekly") {
            expense += amount * (26 / 12);
          } else if (freq === "quarterly" && monthIndex % 3 === 0) {
            expense += amount;
          } else if (freq === "yearly" && monthIndex === 0) {
            expense += amount;
          }
        });
        
        // Add credit payments (assumed monthly)
        expense += summary.monthly_credit_payments || 0;
      }
      
      months.push({
        month: monthLabel,
        income: Math.round(income),
        expenses: Math.round(expense),
        net: Math.round(income - expense),
      });
    }
    
    return months;
  }, [summary, incomeSources, expenses, projectionMode]);

  // Passive income calculation
  const passiveIncomeData = useMemo(() => {
    const passiveSources = incomeSources.filter((src) => src.is_passive && src.is_active);
    const totalMonthly = passiveSources.reduce((sum, src) => sum + toMonthly(src.amount || 0, src.frequency), 0);
    const totalYearly = totalMonthly * 12;
    
    // Group by category
    const byCategory = {};
    passiveSources.forEach((src) => {
      const cat = src.category || "other";
      if (!byCategory[cat]) byCategory[cat] = 0;
      byCategory[cat] += toMonthly(src.amount || 0, src.frequency);
    });
    
    const categoryData = Object.entries(byCategory).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value: Math.round(value),
    }));
    
    return {
      sources: passiveSources,
      totalMonthly,
      totalYearly,
      categoryData,
      count: passiveSources.length,
    };
  }, [incomeSources]);

  if (loading) {
    return (
      <MainLayout>
        <PageLoader />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <TooltipProvider>
        <div className="space-y-6" data-testid="budget-page">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                {t("budget.title") || "Budget"}
              </h1>
              <p className="text-muted-foreground mt-1">{t("budget.subtitle") || "Track your income and expenses"}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => { resetIncomeForm(); setIsIncomeOpen(true); }}
                disabled={isDemo}
                data-testid="header-add-income-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("budget.addIncome") || "Add Income"}
              </Button>
              <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-budget-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Monthly Income */}
            <Card data-testid="income-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      {t("budget.monthlyIncome") || "Monthly Income"}
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="w-3 h-3" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-[200px]">{t("budget.monthlyIncomeTooltip") || "Total recurring income converted to monthly equivalent"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </p>
                    <p className="text-2xl font-bold font-mono text-green-500">
                      +{formatCurrency(summary?.monthly_income || 0)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                    <ArrowDownCircle className="w-6 h-6 text-green-500" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {summary?.income_sources_count || 0} {t("budget.sources") || "sources"}
                </p>
              </CardContent>
            </Card>

            {/* Monthly Expenses */}
            <Card data-testid="expenses-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      {t("budget.monthlyExpenses") || "Monthly Expenses"}
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="w-3 h-3" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-[200px]">{t("budget.monthlyExpensesTooltip") || "User-defined recurring expenses converted to monthly"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </p>
                    <p className="text-2xl font-bold font-mono text-red-500">
                      -{formatCurrency(summary?.monthly_expenses || 0)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                    <ArrowUpCircle className="w-6 h-6 text-red-500" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {summary?.expenses_count || 0} {t("budget.items") || "items"}
                </p>
              </CardContent>
            </Card>

            {/* Credit Payments */}
            <Card data-testid="credit-payments-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      {t("budget.creditPayments") || "Credit Payments"}
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="w-3 h-3" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-[200px]">{t("budget.creditPaymentsTooltip") || "Monthly payments from your credits/liabilities"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </p>
                    <p className="text-2xl font-bold font-mono text-orange-500">
                      -{formatCurrency(summary?.monthly_credit_payments || 0)}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-orange-500" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {summary?.credits_count || 0} {t("budget.credits") || "credits"}
                </p>
              </CardContent>
            </Card>

            {/* Net Cash Flow */}
            <Card data-testid="net-cashflow-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      {t("budget.netCashFlow") || "Net Cash Flow"}
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="w-3 h-3" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-[200px]">{t("budget.netCashFlowTooltip") || "Income minus all expenses and credit payments"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </p>
                    <p className={`text-2xl font-bold font-mono ${(summary?.net_cash_flow || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                      {(summary?.net_cash_flow || 0) >= 0 ? "+" : ""}{formatCurrency(summary?.net_cash_flow || 0)}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${(summary?.net_cash_flow || 0) >= 0 ? "bg-green-500/10" : "bg-red-500/10"}`}>
                    {(summary?.net_cash_flow || 0) >= 0 ? (
                      <TrendingUp className="w-6 h-6 text-green-500" />
                    ) : (
                      <TrendingDown className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {t("budget.perMonth") || "/month"}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Budget Health & Projections Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Budget Health Score */}
            <Card className={`${getHealthBg(summary?.budget_health || 0)}`} data-testid="budget-health-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PiggyBank className="w-5 h-5" />
                  {t("budget.budgetHealth") || "Budget Health"}
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3 h-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs max-w-[250px]">
                        {t("budget.budgetHealthTooltip") || "Based on savings rate: 30%+ = Excellent, 20%+ = Good, 10%+ = Fair, 0%+ = At Risk, Negative = Critical"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className={`text-5xl font-bold font-mono ${getHealthColor(summary?.budget_health || 0)}`}>
                    {summary?.budget_health || 0}
                  </div>
                  <div className="flex-1">
                    <Progress value={summary?.budget_health || 0} className="h-3" />
                    <p className="text-sm mt-2 text-muted-foreground">
                      {t("budget.savingsRate") || "Savings Rate"}: <span className="font-mono font-semibold">{summary?.savings_rate?.toFixed(1) || 0}%</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 6-Month Projection */}
            <Card data-testid="projection-6m-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t("budget.projection6m") || "6-Month Projection"}</CardTitle>
                <CardDescription>{t("budget.projectionDesc") || "Based on current cash flow"}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className={`text-3xl font-bold font-mono ${(summary?.projection_6m || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                  {(summary?.projection_6m || 0) >= 0 ? "+" : ""}{formatCurrency(summary?.projection_6m || 0)}
                </p>
                {(summary?.runway_months && summary?.net_cash_flow < 0) && (
                  <div className="flex items-center gap-2 mt-2 text-amber-500">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">{t("budget.runway") || "Runway"}: {summary?.runway_months} {t("budget.months") || "months"}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 12-Month Projection */}
            <Card data-testid="projection-12m-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t("budget.projection12m") || "12-Month Projection"}</CardTitle>
                <CardDescription>{t("budget.cashReserves") || "Cash Reserves"}: {formatCurrency(summary?.cash_reserves || 0)}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className={`text-3xl font-bold font-mono ${(summary?.projection_12m || 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                  {(summary?.projection_12m || 0) >= 0 ? "+" : ""}{formatCurrency(summary?.projection_12m || 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Predictions Chart & Passive Income Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Predictions Chart - Takes 2 columns */}
            <Card className="lg:col-span-2" data-testid="predictions-chart-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase flex items-center gap-2">
                      <LineChart className="w-5 h-5" />
                      {t("budget.predictionsChart") || "12-Month Forecast"}
                    </CardTitle>
                    <CardDescription>
                      {t("budget.predictionsChartDesc") || "Projected income vs. spending"}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                    <Button
                      variant={projectionMode === "average" ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setProjectionMode("average")}
                      className="text-xs h-7"
                      data-testid="projection-mode-average"
                    >
                      {t("budget.monthlyAvg") || "Monthly Avg"}
                    </Button>
                    <Button
                      variant={projectionMode === "recurring" ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setProjectionMode("recurring")}
                      className="text-xs h-7"
                      data-testid="projection-mode-recurring"
                    >
                      {t("budget.recurring") || "Recurring"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {projectionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={projectionData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#888" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#888" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #333", borderRadius: "8px" }}
                        formatter={(value, name) => [formatCurrency(value), name.charAt(0).toUpperCase() + name.slice(1)]}
                        labelStyle={{ color: "#fff" }}
                      />
                      <Bar dataKey="income" name="income" fill={CHART_COLORS.income} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expenses" name="expenses" fill={CHART_COLORS.expenses} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[280px] text-muted-foreground">
                    <LineChart className="w-12 h-12 mb-2 opacity-30" />
                    <p>{t("budget.noDataForChart") || "Add income or expenses to see projections"}</p>
                  </div>
                )}
                {projectionData.length > 0 && (
                  <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_COLORS.income }} />
                      <span className="text-muted-foreground">{t("budget.income") || "Income"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: CHART_COLORS.expenses }} />
                      <span className="text-muted-foreground">{t("budget.totalExpenses") || "Total Expenses"}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Passive Income Card */}
            <Card data-testid="passive-income-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-500" />
                  {t("budget.passiveIncome") || "Passive Income"}
                </CardTitle>
                <CardDescription>
                  {t("budget.passiveIncomeDesc") || "Income that requires minimal effort"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {passiveIncomeData.count > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
                        <p className="text-xs text-muted-foreground mb-1">{t("budget.monthly") || "Monthly"}</p>
                        <p className="text-xl font-bold font-mono text-purple-400">
                          +{formatCurrency(passiveIncomeData.totalMonthly)}
                        </p>
                      </div>
                      <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
                        <p className="text-xs text-muted-foreground mb-1">{t("budget.yearly") || "Yearly"}</p>
                        <p className="text-xl font-bold font-mono text-purple-400">
                          +{formatCurrency(passiveIncomeData.totalYearly)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Passive Income Sources List */}
                    <div className="space-y-2 max-h-[140px] overflow-y-auto">
                      {passiveIncomeData.sources.map((src) => (
                        <div key={src.income_id} className="flex items-center justify-between text-sm py-1 border-b border-border/50 last:border-0">
                          <div>
                            <p className="font-medium truncate max-w-[120px]">{src.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{src.category}</p>
                          </div>
                          <p className="font-mono text-purple-400">
                            +{formatCurrency(toMonthly(src.amount, src.frequency))}<span className="text-xs text-muted-foreground">/mo</span>
                          </p>
                        </div>
                      ))}
                    </div>
                    
                    {summary?.monthly_income > 0 && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-xs text-muted-foreground">
                          {t("budget.passiveRatio") || "Passive ratio"}: <span className="font-mono font-semibold text-purple-400">
                            {((passiveIncomeData.totalMonthly / summary.monthly_income) * 100).toFixed(1)}%
                          </span> {t("budget.ofTotalIncome") || "of total income"}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                    <DollarSign className="w-10 h-10 mb-2 opacity-30" />
                    <p className="text-sm text-center">{t("budget.noPassiveIncome") || "No passive income yet"}</p>
                    <p className="text-xs text-center mt-1 text-muted-foreground/70">
                      {t("budget.markAsPassive") || "Mark income sources as passive when adding"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Income & Expenses Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Income Sources */}
            <Card data-testid="income-sources-section">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase">{t("budget.incomeSources") || "Income Sources"}</CardTitle>
                    <CardDescription>{t("budget.incomeSourcesDesc") || "Recurring income streams"}</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => { resetIncomeForm(); setIsIncomeOpen(true); }}
                    disabled={isDemo}
                    data-testid="add-income-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t("budget.addIncome") || "Add"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {incomeSources.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("budget.source") || "Source"}</TableHead>
                        <TableHead>{t("budget.category") || "Category"}</TableHead>
                        <TableHead className="text-right">{t("budget.monthly") || "Monthly"}</TableHead>
                        <TableHead className="text-right">{t("budget.actions") || "Actions"}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {incomeSources.map((income) => (
                        <TableRow key={income.income_id} data-testid={`income-row-${income.income_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{income.name}</p>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-muted-foreground capitalize">{income.frequency}</span>
                                <span className="text-[10px] font-mono text-muted-foreground/60">{income.currency || "USD"}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize text-xs">{income.category}</Badge>
                              {income.is_passive && (
                                <Badge variant="secondary" className="text-[10px] bg-purple-500/20 text-purple-400 border-purple-500/30">
                                  {t("budget.passive") || "Passive"}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-green-500">
                            +{formatCurrency(toMonthly(income.amount, income.frequency), true, income.currency || "USD")}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" disabled={isDemo}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditIncome(income)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  {t("budget.edit") || "Edit"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => confirmDelete(income, "income")} className="text-destructive">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  {t("budget.delete") || "Delete"}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ArrowDownCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>{t("budget.noIncome") || "No income sources yet"}</p>
                    <Button variant="link" onClick={() => { resetIncomeForm(); setIsIncomeOpen(true); }} disabled={isDemo}>
                      {t("budget.addFirst") || "Add your first income source"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expenses */}
            <Card data-testid="expenses-section">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase">{t("budget.expenses") || "Expenses"}</CardTitle>
                    <CardDescription>{t("budget.expensesDesc") || "Recurring outflows"}</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => { resetExpenseForm(); setIsExpenseOpen(true); }}
                    disabled={isDemo}
                    data-testid="add-expense-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t("budget.addExpense") || "Add"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {expenses.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("budget.expense") || "Expense"}</TableHead>
                        <TableHead>{t("budget.category") || "Category"}</TableHead>
                        <TableHead className="text-right">{t("budget.monthly") || "Monthly"}</TableHead>
                        <TableHead className="text-right">{t("budget.actions") || "Actions"}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map((expense) => (
                        <TableRow key={expense.expense_id} data-testid={`expense-row-${expense.expense_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{expense.name}</p>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-muted-foreground capitalize">{expense.frequency}</span>
                                <span className="text-[10px] font-mono text-muted-foreground/60">{expense.currency || "USD"}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize text-xs">{expense.category}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono text-red-500">
                            -{formatCurrency(toMonthly(expense.amount, expense.frequency), true, expense.currency || "USD")}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" disabled={isDemo}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditExpense(expense)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  {t("budget.edit") || "Edit"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => confirmDelete(expense, "expense")} className="text-destructive">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  {t("budget.delete") || "Delete"}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ArrowUpCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>{t("budget.noExpenses") || "No expenses yet"}</p>
                    <Button variant="link" onClick={() => { resetExpenseForm(); setIsExpenseOpen(true); }} disabled={isDemo}>
                      {t("budget.addFirstExpense") || "Add your first expense"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Credit Payments Breakdown */}
          {summary?.credit_payments?.length > 0 && (
            <Card data-testid="credit-payments-breakdown">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase">{t("budget.creditPaymentsBreakdown") || "Credit Payments Breakdown"}</CardTitle>
                <CardDescription>{t("budget.creditPaymentsBreakdownDesc") || "Monthly payments from your liabilities"}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("budget.creditName") || "Credit"}</TableHead>
                      <TableHead>{t("budget.type") || "Type"}</TableHead>
                      <TableHead className="text-right">{t("budget.monthlyPayment") || "Monthly Payment"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.credit_payments.map((credit, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{credit.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">{credit.type?.replace("_", " ")}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-orange-500">
                          -{formatCurrency(credit.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Add/Edit Income Dialog */}
          <Dialog open={isIncomeOpen} onOpenChange={(open) => { if (!open) resetIncomeForm(); setIsIncomeOpen(open); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] uppercase">
                  {editingItem ? t("budget.editIncome") || "Edit Income" : t("budget.addIncome") || "Add Income"}
                </DialogTitle>
                <DialogDescription>{t("budget.incomeFormDesc") || "Enter details about this income source"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>{t("budget.name") || "Name"}</Label>
                  <Input
                    placeholder="e.g., Monthly Salary, Rental Income..."
                    value={incomeForm.name}
                    onChange={(e) => setIncomeForm({ ...incomeForm, name: e.target.value })}
                    data-testid="income-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("budget.category") || "Category"}</Label>
                    <Select value={incomeForm.category} onValueChange={(v) => setIncomeForm({ ...incomeForm, category: v })}>
                      <SelectTrigger data-testid="income-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INCOME_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("budget.frequency") || "Frequency"}</Label>
                    <Select value={incomeForm.frequency} onValueChange={(v) => setIncomeForm({ ...incomeForm, frequency: v })}>
                      <SelectTrigger data-testid="income-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("budget.amount") || "Amount"}</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="0.00"
                      value={incomeForm.amount}
                      onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                      data-testid="income-amount-input"
                    />
                  </div>
                  <div>
                    <Label>{t("budget.currency") || "Currency"}</Label>
                    <Select value={incomeForm.currency} onValueChange={(v) => setIncomeForm({ ...incomeForm, currency: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="CHF">CHF</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>{t("budget.startDate") || "Start Date"} ({t("budget.optional") || "optional"})</Label>
                  <Input
                    type="date"
                    value={incomeForm.start_date}
                    onChange={(e) => setIncomeForm({ ...incomeForm, start_date: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <input
                    type="checkbox"
                    id="is_passive"
                    checked={incomeForm.is_passive}
                    onChange={(e) => setIncomeForm({ ...incomeForm, is_passive: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 text-purple-500 focus:ring-purple-500 bg-background"
                    data-testid="income-passive-checkbox"
                  />
                  <div>
                    <Label htmlFor="is_passive" className="cursor-pointer font-medium text-sm">
                      {t("budget.passiveIncomeLabel") || "This is passive income"}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("budget.passiveIncomeHint") || "e.g., dividends, rental, interest - income that requires minimal effort"}
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { resetIncomeForm(); setIsIncomeOpen(false); }}>
                  {t("budget.cancel") || "Cancel"}
                </Button>
                <Button onClick={handleSaveIncome} data-testid="save-income-btn">
                  {editingItem ? t("budget.update") || "Update" : t("budget.add") || "Add"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add/Edit Expense Dialog */}
          <Dialog open={isExpenseOpen} onOpenChange={(open) => { if (!open) resetExpenseForm(); setIsExpenseOpen(open); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] uppercase">
                  {editingItem ? t("budget.editExpense") || "Edit Expense" : t("budget.addExpense") || "Add Expense"}
                </DialogTitle>
                <DialogDescription>{t("budget.expenseFormDesc") || "Enter details about this expense"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>{t("budget.name") || "Name"}</Label>
                  <Input
                    placeholder="e.g., Netflix, Rent, Electric Bill..."
                    value={expenseForm.name}
                    onChange={(e) => setExpenseForm({ ...expenseForm, name: e.target.value })}
                    data-testid="expense-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("budget.category") || "Category"}</Label>
                    <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                      <SelectTrigger data-testid="expense-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPENSE_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("budget.frequency") || "Frequency"}</Label>
                    <Select value={expenseForm.frequency} onValueChange={(v) => setExpenseForm({ ...expenseForm, frequency: v })}>
                      <SelectTrigger data-testid="expense-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("budget.amount") || "Amount"}</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="0.00"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                      data-testid="expense-amount-input"
                    />
                  </div>
                  <div>
                    <Label>{t("budget.currency") || "Currency"}</Label>
                    <Select value={expenseForm.currency} onValueChange={(v) => setExpenseForm({ ...expenseForm, currency: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="CHF">CHF</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>{t("budget.startDate") || "Start Date"} ({t("budget.optional") || "optional"})</Label>
                  <Input
                    type="date"
                    value={expenseForm.start_date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, start_date: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { resetExpenseForm(); setIsExpenseOpen(false); }}>
                  {t("budget.cancel") || "Cancel"}
                </Button>
                <Button onClick={handleSaveExpense} data-testid="save-expense-btn">
                  {editingItem ? t("budget.update") || "Update" : t("budget.add") || "Add"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <DeleteConfirmDialog
            open={isDeleteOpen}
            onOpenChange={setIsDeleteOpen}
            onConfirm={handleDelete}
            title={deleteType === "income" ? t("budget.deleteIncome") || "Delete Income" : t("budget.deleteExpense") || "Delete Expense"}
            itemName={deleteTarget?.name}
          />
        </div>
      </TooltipProvider>
    </MainLayout>
  );
};

export default BudgetPage;
